import type { OktaAuth } from '@okta/okta-auth-js';
import { putFetch } from './put-fetch';
import { HttpError } from './request';

/**
 * PUT that survives a stale cached token, the same way postFetchWithRenewal
 * does: a 401 usually means the token in sessionStorage went stale, so renew
 * once and retry; if renewal fails, fall back to full re-authentication. Any
 * other error propagates untouched.
 *
 * Deliberate near-duplicate of postFetchWithRenewal — worth folding the two
 * into one method-agnostic helper if a third verb needs it.
 */
export const putFetchWithRenewal = async <T>(
	oktaAuth: OktaAuth,
	token: string | null | undefined,
	url: string,
	body?: Record<string, unknown>
): Promise<T> => {
	try {
		return await putFetch<T>(token, url, body);
	} catch (error) {
		if (error instanceof HttpError && error.status === 401) {
			const renewedToken = await oktaAuth.getOrRenewAccessToken().catch(() => null);

			if (renewedToken) {
				return await putFetch<T>(renewedToken, url, body);
			}

			await oktaAuth.signInWithRedirect();
		}

		throw error;
	}
};
